import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import api from "../../api/api";
import "./ProjectPage.css";
import BackButton from "../../components/BackButton/BackButton";

/* ── constants ───────────────────────────────────────────── */
const STATUS_OPTIONS = [
  { value: "planned",     icon: "📋", label: "Planned",     color: "#64748b", bg: "rgba(100,116,139,0.1)",  border: "rgba(100,116,139,0.25)" },
  { value: "in_progress", icon: "⚡", label: "In Progress", color: "#f59e0b", bg: "rgba(245,158,11,0.1)",   border: "rgba(245,158,11,0.3)"   },
  { value: "completed",   icon: "✅", label: "Completed",   color: "#059669", bg: "rgba(5,150,105,0.1)",    border: "rgba(5,150,105,0.3)"    },
];

const ProjectPage = () => {
  const { roadmapId }    = useParams();
  const [searchParams]   = useSearchParams();
  const navigate         = useNavigate();

  // If ?itemId= is present → specific project node was clicked
  // Otherwise → fetch current active project (Continue Learning flow)
  const itemId = searchParams.get("itemId");

  const [project,       setProject]       = useState(null);
  const [loading,       setLoading]       = useState(true);
  const [completing,    setCompleting]    = useState(false);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [statusMsg,     setStatusMsg]     = useState("");

  // Is this a "view only" visit (not the current active step)?
  const isViewOnly = !!itemId;

  /* ── Fetch ─────────────────────────────────────────────── */
  useEffect(() => {
    const fetch = async () => {
      try {
        if (itemId) {
          // Specific project node clicked — fetch that exact item
          const res = await api.get(`/roadmap/${roadmapId}/items/${itemId}`);
          setProject(res.data.project);
        } else {
          // Continue Learning flow — fetch current active project
          const res = await api.get(`/roadmap/current-project/${roadmapId}`);
          setProject(res.data.project);
        }
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [roadmapId, itemId]);

  /* ── Update status ──────────────────────────────────────── */
  const handleStatusChange = async (newStatus) => {
    if (!project || newStatus === project.status) return;
    if (newStatus === "completed") { handleComplete(); return; }

    setUpdatingStatus(true);
    try {
      await api.patch(`/roadmap/${roadmapId}/items/${project._id}`, {
        status: newStatus,
      });
      setProject((prev) => ({ ...prev, status: newStatus }));
      setStatusMsg("Status updated");
      setTimeout(() => setStatusMsg(""), 2000);
    } catch (err) {
      console.error(err);
      setStatusMsg("Failed to update status");
      setTimeout(() => setStatusMsg(""), 2500);
    } finally {
      setUpdatingStatus(false);
    }
  };

  /* ── Mark complete ──────────────────────────────────────── */
  const handleComplete = async () => {
    if (!project) return;
    setCompleting(true);
    try {
      await api.post("/roadmap/complete-project", {
        roadmapId,
        projectId: project._id,
      });
      navigate("/dashboard");
    } catch (err) {
      console.error(err);
      setStatusMsg("Failed to mark complete");
      setTimeout(() => setStatusMsg(""), 2500);
    } finally {
      setCompleting(false);
    }
  };

  /* ── Loading ────────────────────────────────────────────── */
  if (loading) {
    return (
      <div className="pp-page pp-page--center">
        <div className="pp-spinner" />
        <p>Loading project…</p>
      </div>
    );
  }

  if (!project) {
    return (
      <div className="pp-page pp-page--center">
        <div className="pp-empty-icon">🚀</div>
        <h2>Project not found</h2>
        <p>This project could not be loaded.</p>
        <BackButton />
      </div>
    );
  }

  const currentStatus = STATUS_OPTIONS.find(s => s.value === (project.status || "planned")) || STATUS_OPTIONS[0];
  const isCompleted   = project.completed || project.status === "completed";

  /* ── Render ─────────────────────────────────────────────── */
  return (
    <div className="pp-page">
      <div className="pp-container">

        {/* Back button */}
        <BackButton
          to={`/roadmap/${roadmapId}`}
          label="Back to Roadmap"
        />

        {/* View-only notice */}
        {isViewOnly && !isCompleted && (
          <div className="pp-view-only-banner">
            👁 You're viewing this project's details. To mark it complete, it must be your current roadmap step.
          </div>
        )}

        <div className="pp-card">

          {/* Completed banner */}
          {isCompleted && (
            <div className="pp-completed-banner">
              ✅ This project is marked as completed
            </div>
          )}

          {/* Header */}
          <div className="pp-card-header">
            <div className="pp-type-badge">🚀 Project</div>
            <h1 className="pp-title">{project.name}</h1>
            <div
              className="pp-status-badge"
              style={{ color: currentStatus.color, background: currentStatus.bg, borderColor: currentStatus.border }}
            >
              {currentStatus.icon} {currentStatus.label}
              {updatingStatus && <span className="pp-status-spinner" />}
            </div>
          </div>

          {/* Description */}
          {project.description && (
            <p className="pp-description">{project.description}</p>
          )}

          {/* Tech stack */}
          {project.techStack?.length > 0 && (
            <div className="pp-section">
              <h3 className="pp-section-label">Tech Stack</h3>
              <div className="pp-tech-row">
                {project.techStack.map((t) => (
                  <span key={t} className="pp-tech-chip">{t}</span>
                ))}
              </div>
            </div>
          )}

          {/* Links */}
          {(project.githubUrl || project.liveUrl) && (
            <div className="pp-section">
              <h3 className="pp-section-label">Links</h3>
              <div className="pp-links-row">
                {project.githubUrl && (
                  <a
                    href={project.githubUrl.startsWith("http") ? project.githubUrl : `https://${project.githubUrl}`}
                    target="_blank" rel="noreferrer"
                    className="pp-link pp-link--github"
                  >
                    <span className="pp-link-icon">⚙</span>
                    <div>
                      <span className="pp-link-label">GitHub Repository</span>
                      <span className="pp-link-url">{project.githubUrl}</span>
                    </div>
                    <span className="pp-link-arrow">↗</span>
                  </a>
                )}
                {project.liveUrl && (
                  <a
                    href={project.liveUrl.startsWith("http") ? project.liveUrl : `https://${project.liveUrl}`}
                    target="_blank" rel="noreferrer"
                    className="pp-link pp-link--live"
                  >
                    <span className="pp-link-icon">🌐</span>
                    <div>
                      <span className="pp-link-label">Live Demo</span>
                      <span className="pp-link-url">{project.liveUrl}</span>
                    </div>
                    <span className="pp-link-arrow">↗</span>
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Status picker — always shown so user can update even in view mode */}
          <div className="pp-section">
            <h3 className="pp-section-label">Update Status</h3>
            <div className="pp-status-picker">
              {STATUS_OPTIONS.map((s) => (
                <button
                  key={s.value}
                  className={`pp-status-btn ${project.status === s.value ? "pp-status-btn--active" : ""}`}
                  style={project.status === s.value
                    ? { borderColor: s.color, color: s.color, background: s.bg }
                    : {}}
                  onClick={() => handleStatusChange(s.value)}
                  disabled={updatingStatus || isCompleted}
                >
                  {s.icon} {s.label}
                </button>
              ))}
            </div>
            {statusMsg && (
              <p className={`pp-status-msg ${statusMsg.includes("Failed") ? "pp-status-msg--error" : "pp-status-msg--ok"}`}>
                {statusMsg}
              </p>
            )}
          </div>

          <div className="pp-divider" />

          {/* Complete action */}
          {!isCompleted ? (
            <div className="pp-complete-section">
              <div className="pp-complete-info">
                <strong>Ready to finish?</strong>
                <p>
                  {isViewOnly
                    ? "Complete this from the roadmap when it becomes your active step."
                    : "Marking this project complete will advance your roadmap to the next step."}
                </p>
              </div>
              {/* Only show complete button when it's the active step */}
              {!isViewOnly && (
                <button
                  className="pp-btn pp-btn--complete"
                  onClick={handleComplete}
                  disabled={completing}
                >
                  {completing
                    ? <><span className="pp-spinner-sm" /> Completing…</>
                    : "✓ Mark Project Complete"}
                </button>
              )}
            </div>
          ) : (
            <div className="pp-already-done">
              <span>🎉 Project completed — great work!</span>
              <button
                className="pp-btn pp-btn--secondary"
                onClick={() => navigate("/dashboard")}
              >
                Back to Dashboard
              </button>
            </div>
          )}

        </div>
      </div>
    </div>
  );
};

export default ProjectPage;